import React from 'react'
import { Button, Table, TableProps, Tooltip } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { MenuOutlined } from '@ant-design/icons'
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

type TSortable = ReturnType<typeof useSortable>

const SortableIdsContext = React.createContext<Set<string>>(new Set())
const RowContext = React.createContext<Partial<Pick<TSortable, 'setActivatorNodeRef' | 'listeners'>>>({})

const DragHandle = () => {
  const { setActivatorNodeRef, listeners } = React.useContext(RowContext)
  return (
    <Button
      type='text'
      size='small'
      aria-label='Перетащить'
      icon={<MenuOutlined />}
      ref={setActivatorNodeRef}
      style={{ cursor: 'grab' }}
      // после перетаскивания клик не должен открывать строку
      onClick={(e) => e.stopPropagation()}
      {...listeners}
    />
  )
}

interface IRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string
}

const SortableRow = (props: IRowProps) => {
  const sortableIds = React.useContext(SortableIdsContext)
  const id = props['data-row-key']
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled: !sortableIds.has(id) })
  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging && {
      position: 'relative',
      zIndex: 10,
      background: '#fff',
      boxShadow: '0 6px 16px rgba(0, 0, 0, 0.12)'
    })
  }
  const contextValue = React.useMemo(() => ({ setActivatorNodeRef, listeners }), [setActivatorNodeRef, listeners])

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  )
}

// Служебные строки таблицы (например «Нет данных») приходят без ключа
const Row = (props: IRowProps) => (props['data-row-key'] ? <SortableRow {...props} /> : <tr {...props} />)

// Закреплённые и прочие строки вне списка остаются на местах, перетаскиваемые встают
// в освободившиеся по новому порядку
export const reorderRows = <T extends { _id: string }>(rows: T[], ids: string[]): T[] => {
  const byId = new Map(rows.map((row) => [row._id, row]))
  const sortable = new Set(ids)
  let next = 0
  return rows.map((row) => (sortable.has(row._id) ? (byId.get(ids[next++]) as T) : row))
}

interface ISortableTableProps<T> extends Omit<TableProps<T>, 'columns' | 'components' | 'rowKey'> {
  columns: ColumnsType<T>
  // строки, которые можно перетаскивать, в текущем порядке
  sortableIds: string[]
  // строки с неактивной ручкой и подсказкой, почему их не перетащить
  lockedIds?: string[]
  lockedHint?: string
  onSortEnd: (ids: string[]) => void
}

// Таблица, строки которой перетаскиваются за ручку ☰ в первом столбце
export const SortableTable = <T extends { _id: string }>({
  columns,
  sortableIds,
  lockedIds = [],
  lockedHint,
  onSortEnd,
  ...props
}: ISortableTableProps<T>) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))
  const sortableSet = React.useMemo(() => new Set(sortableIds), [sortableIds])
  const lockedSet = React.useMemo(() => new Set(lockedIds), [lockedIds])

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    const from = sortableIds.indexOf(String(active.id))
    const to = sortableIds.indexOf(String(over.id))
    if (from < 0 || to < 0) return
    onSortEnd(arrayMove(sortableIds, from, to))
  }

  const handleColumn: ColumnsType<T>[number] = {
    key: 'sort',
    width: 48,
    align: 'center',
    render: (_, record) => {
      if (sortableSet.has(record._id)) return <DragHandle />
      if (!lockedSet.has(record._id)) return null
      return (
        <Tooltip title={lockedHint}>
          <Button
            type='text'
            size='small'
            aria-label={lockedHint}
            icon={<MenuOutlined />}
            disabled
            onClick={(e) => e.stopPropagation()}
          />
        </Tooltip>
      )
    }
  }

  return (
    <SortableIdsContext.Provider value={sortableSet}>
      <DndContext
        sensors={sensors}
        modifiers={[restrictToVerticalAxis]}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          <Table<T>
            {...props}
            rowKey='_id'
            columns={[handleColumn, ...columns]}
            components={{ body: { row: Row } }}
          />
        </SortableContext>
      </DndContext>
    </SortableIdsContext.Provider>
  )
}
